import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scan, Mail, Lock, User, Eye, EyeOff, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_CLIENT_ID = '80826182913-ek2qj2d0fgrjd8g8gm78g13u02u14j3h.apps.googleusercontent.com';

export default function AuthPage({ onSuccess, onBack }) {
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const payload = mode === 'login' ? { email, password } : { email, password, name };
      const res = await axios.post(`${API_URL}${endpoint}`, payload);
      localStorage.setItem('veritas_token', res.data.token);
      localStorage.setItem('veritas_user', JSON.stringify(res.data.user));
      if (mode === 'register' && res.data.email_verification_sent) {
        setVerificationEmail(email);
        setVerificationSent(true);
      } else {
        onSuccess(res.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al autenticar');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const token = localStorage.getItem('veritas_token');
      await axios.post(`${API_URL}/api/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResendDone(true);
      setTimeout(() => setResendDone(false), 4000);
    } catch {}
    finally { setResending(false); }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/google`, {
        token: credentialResponse.credential
      });
      localStorage.setItem('veritas_token', res.data.token);
      localStorage.setItem('veritas_user', JSON.stringify(res.data.user));
      onSuccess(res.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al autenticar con Google');
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 relative">
        <div className="scanlines" />
        <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.08), transparent 60%)' }} />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-md">
          <div className="text-center mb-8">
            <button onClick={onBack} className="inline-flex items-center gap-3 hover:opacity-80 transition-opacity">
              <Scan className="w-8 h-8 text-blue-500" />
              <span className="font-heading text-2xl font-black">VERITAS <span className="text-blue-500">SCAN</span></span>
            </button>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center space-y-6 shadow-2xl">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 12, delay: 0.15 }}
              className="w-20 h-20 mx-auto rounded-full bg-blue-500/10 border-2 border-blue-500/30 flex items-center justify-center">
              <Mail className="w-10 h-10 text-blue-400" />
            </motion.div>
            <div>
              <h1 className="font-heading text-2xl font-black text-white mb-3">Revisa tu email</h1>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Hemos enviado un enlace de verificación a:<br />
                <strong className="text-white font-mono">{verificationEmail}</strong>
              </p>
            </div>
            <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-4 text-left space-y-2.5">
              {[
                'Abre tu bandeja de entrada',
                'Busca el email de Veritas Scan',
                'Haz clic en "Verificar mi email"',
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-mono text-xs font-bold">{i + 1}</span>
                  </div>
                  <span className="text-zinc-300 text-sm">{step}</span>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <button onClick={handleResend} disabled={resending || resendDone}
                className="w-full flex items-center justify-center gap-2 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white font-mono text-sm rounded-xl transition-all disabled:opacity-60">
                {resending ? <><RefreshCw className="w-4 h-4 animate-spin" />Reenviando...</> :
                  resendDone ? <><CheckCircle className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400">Enviado</span></> :
                  <><RefreshCw className="w-4 h-4" />Reenviar email</>}
              </button>
              <button onClick={() => onSuccess(JSON.parse(localStorage.getItem('veritas_user')))}
                className="w-full py-2.5 text-zinc-600 hover:text-zinc-400 font-mono text-xs transition-colors">
                Continuar sin verificar →
              </button>
            </div>
          </div>
          <p className="text-center text-zinc-600 text-xs font-mono mt-4">Revisa también la carpeta de spam</p>
        </motion.div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center relative">
        <div className="scanlines" />
        <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.08), transparent 60%)' }} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md px-4 sm:px-6"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <button onClick={onBack} className="inline-flex items-center gap-3 mb-6">
              <Scan className="w-8 h-8 text-blue-500" />
              <span className="font-heading text-2xl font-black">VERITAS <span className="text-blue-500">SCAN</span></span>
            </button>
            <h1 className="font-heading text-3xl font-black">
              {mode === 'login' ? 'Bienvenido de nuevo' : 'Crear cuenta'}
            </h1>
            <p className="text-zinc-500 mt-2 text-sm">
              {mode === 'login' ? 'Inicia sesión para continuar' : 'Empieza gratis hoy'}
            </p>
          </div>

          {/* Card */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 space-y-4">

            {/* Tabs */}
            <div className="flex border border-zinc-800 rounded-sm overflow-hidden mb-2">
              {['login', 'register'].map(m => (
                <button
                  key={m}
                  onClick={() => { setMode(m); setError(null); }}
                  className={`flex-1 py-2 font-mono text-xs uppercase tracking-wider transition-all ${mode === m ? 'bg-blue-600 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  {m === 'login' ? 'Iniciar sesión' : 'Registrarse'}
                </button>
              ))}
            </div>

            {/* Google button */}
            <div className="flex justify-center py-1">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Error al autenticar con Google')}
                theme="filled_black"
                shape="rectangular"
                size="large"
                text={mode === 'login' ? 'signin_with' : 'signup_with'}
                locale="es"
                width="100%"
              />
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-zinc-800" />
              <span className="text-zinc-600 font-mono text-xs">o con email</span>
              <div className="flex-1 h-px bg-zinc-800" />
            </div>

            {/* Name field (register only) */}
            <AnimatePresence mode="wait">
              {mode === 'register' && (
                <motion.div key="name" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                  <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Nombre</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Tu nombre"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-blue-500 font-mono text-sm transition-colors"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Email */}
            <div>
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  placeholder="tu@email.com"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-blue-500 font-mono text-sm transition-colors"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  placeholder="••••••••"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-10 py-3 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-blue-500 font-mono text-sm transition-colors"
                />
                <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={loading || !email || !password || (mode === 'register' && !name)}
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono uppercase tracking-wider py-3 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {loading
                ? <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" />
                : mode === 'login' ? 'Iniciar sesión' : 'Crear cuenta'
              }
            </button>

            <p className="text-center text-zinc-600 text-xs pt-1">
              {mode === 'login' ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}{' '}
              <button
                onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(null); }}
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                {mode === 'login' ? 'Regístrate gratis' : 'Inicia sesión'}
              </button>
            </p>
          </div>

          <div className="mt-4 text-center text-zinc-600 text-xs font-mono">
            Plan gratuito: 5 análisis/día por dispositivo • Sin tarjeta requerida
          </div>
        </motion.div>
      </div>
    </GoogleOAuthProvider>
  );
}
